export interface ThemePalette {
  background: string;
  surface: string;
  border: string;
  primary: string;
  secondary: string;
  tertiary: string;
  textMain: string;
  textMuted: string;
  isDark: boolean;
}

export const PALETTES: Record<string, ThemePalette> = {
  // Placeholder for custom theme - will be populated dynamically
  CUSTOM: {
    background: '#0f172a',
    surface: '#1e293b',
    border: '#334155',
    primary: '#3b82f6',
    secondary: '#94a3b8',
    tertiary: '#059669',
    textMain: '#f1f5f9',
    textMuted: '#64748b',
    isDark: true,
  },
  LIGHT_CONSERVATIVE: {
    background: '#ffffff',
    surface: '#f8fafc',
    border: '#e2e8f0',
    primary: '#2563eb',
    secondary: '#64748b',
    tertiary: '#059669',
    textMain: '#0f172a',
    textMuted: '#64748b',
    isDark: false,
  },
  DARK_CONSERVATIVE: {
    background: '#0f172a',
    surface: '#1e293b',
    border: '#334155',
    primary: '#3b82f6',
    secondary: '#94a3b8',
    tertiary: '#10b981',
    textMain: '#f1f5f9',
    textMuted: '#94a3b8',
    isDark: true,
  },
  HIGH_CONTRAST_DARK: {
    background: '#1a1033',
    surface: '#2d1b4d',
    border: '#4c3077',
    primary: '#a855f7',
    secondary: '#fbbf24',
    tertiary: '#10b981',
    textMain: '#e9d5ff',
    textMuted: '#a78bfa',
    isDark: true,
  },
  HIGH_CONTRAST_LIGHT: {
    background: '#fcfaf2',
    surface: '#f3efdf',
    border: '#d4c9a8',
    primary: '#7e22ce',
    secondary: '#b45309',
    tertiary: '#059669',
    textMain: '#2d1a12',
    textMuted: '#7c6a5e',
    isDark: false,
  },
  VIVID_DARK: {
    background: '#050505',
    surface: '#000000',
    border: '#00f2ff',
    primary: '#ff00ff',
    secondary: '#00f2ff',
    tertiary: '#10b981',
    textMain: '#ffffff',
    textMuted: '#00f2ff',
    isDark: true,
  },
};

export const themeColors = PALETTES.DARK_CONSERVATIVE;

export const theme = {
  colors: themeColors,
  fonts: {
    fantasy: 'EagleLake',
  },
};

export const getGlobalCSS = (colors: ThemePalette) => `
  * { box-sizing: border-box; }

  body {
    margin: 0;
    padding: 0;
    font-family: Arial, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, sans-serif;
    background-color: ${colors.background};
    color: ${colors.textMain};
    overflow-x: hidden;
  }

  /* Background Image */
  .app-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-image: url('/assets/via_logos_background.png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.6;
    z-index: 0;
    pointer-events: none;
  }

  /* Dark Overlay */
  .app-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: ${colors.background};
    opacity: 0.3;
    z-index: 1;
    pointer-events: none;
  }

  /* App Container - must be above background */
  .app-container {
    position: relative;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    z-index: 2;
  }

  .fantasy-font { font-family: 'EagleLake', serif; }

  .glass-card {
    background-color: rgba(30, 41, 59, 0.7) !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }

  /* Text shadows for better readability on backgrounds */
  .text-shadow {
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.8);
  }

  .text-shadow-light {
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.6);
  }

  /* Button styles matching mobile */
  .btn-primary {
    background-color: ${colors.primary};
    color: #ffffff;
    padding: 18px 40px;
    border-radius: 30px;
    font-weight: bold;
    border: none;
    cursor: pointer;
    transition: transform 0.2s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  .btn-primary:hover {
    transform: scale(1.05);
  }

  .btn-secondary {
    background-color: transparent;
    color: ${colors.primary};
    padding: 18px 40px;
    border-radius: 30px;
    font-weight: bold;
    border: 2px solid ${colors.primary};
    cursor: pointer;
    transition: transform 0.2s ease;
  }

  .btn-secondary:hover {
    transform: scale(1.05);
  }

  /* Input styles matching mobile */
  input, textarea, select {
    background-color: ${colors.surface};
    border: 1px solid ${colors.border};
    color: ${colors.textMain};
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 16px;
    font-family: inherit;
  }

  input:focus, textarea:focus, select:focus {
    outline: 2px solid ${colors.primary};
    outline-offset: -2px;
  }

  input::placeholder, textarea::placeholder {
    color: ${colors.textMuted};
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${colors.background};
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.border};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors.textMuted};
  }
`;

// Always inject CSS for web (standard React, not react-native-web)
if (typeof document !== 'undefined') {
  const styleId = 'dream-catcher-styles';
  let style = document.getElementById(styleId);
  if (!style) {
    style = document.createElement('style');
    style.id = styleId;
    document.head.appendChild(style);
  }
  style.textContent = getGlobalCSS(themeColors);
}

export const appStyles = {
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  safeArea: {
    flex: 1,
  },
  gameContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderText: {
    color: theme.colors.primary,
    fontSize: 24,
    fontWeight: 'bold',
    fontFamily: theme.fonts.fantasy,
  },
};

export const commonStyles = {
  messageBubble: {
    padding: 15,
    borderRadius: 12,
    marginBottom: 15,
    maxWidth: '85%',
  },
  // CHANGED: Removed absolute positioning so it flows naturally in the footer column
  bannerContainer: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 16,
    alignSelf: 'stretch',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  },
  bannerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  adTitle: {
    fontWeight: 'bold',
    fontSize: 14,
    marginBottom: 2,
  },
  upgradeButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  upgradeText: {
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: 12,
  },
  scrollContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    width: '100%',
  },
  card: {
    width: '90%',
    maxWidth: 600,
    backgroundColor: theme.colors.surface,
    padding: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.border,
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.3)',
  },
  iconButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonTextSmall: {
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 32,
    fontFamily: theme.fonts.fantasy,
  },
  inputGroup: {
    gap: 24,
  },
  row: {
    flexDirection: 'row',
    gap: 16,
  },
  flex1: {
    flex: 1,
  },
  fieldContainer: {
    width: '100%',
  },
  label: {
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  rowWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  inputWithIcon: {
    flex: 1,
    marginRight: 0,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 24,
  },
  primaryButton: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: 12,
    alignItems: 'center',
  },
  secondaryButton: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: 12,
    alignItems: 'center',
  },
  buttonText: {
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: 18,
  },
  primaryButtonText: {
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  secondaryButtonText: {
    fontWeight: 'bold',
    fontSize: 18,
  },
  bannerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  bannerTitle: {
    fontWeight: 'bold',
    fontSize: 14,
  },
  closeButton: {
    padding: 4,
  },
  closeText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  bannerDescription: {
    fontSize: 12,
    lineHeight: 16,
    marginBottom: 12,
  },
  mainContainer: {
    flex: 1,
    flexDirection: 'row',
    // maxWidth removed - now constrained by parent mobile container
    width: '100%',
  },
  sidebar: {
    width: 280,
    borderRightWidth: 1,
    // Note: display: 'none' removed - web now uses modal overlay sidebar
  },
  contentArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  },
  sidebarHeader: {
    padding: 20,
    borderBottomWidth: 1,
  },
  sidebarScroll: {
    flex: 1,
    padding: 20,
  },
  directiveInput: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    fontSize: 14,
    height: 200,
    textAlignVertical: 'top',
    marginTop: 8,
  },
  narrativeScroll: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  narrativeContent: {
    padding: 20,
    paddingBottom: 20,
  },
  messageContainer: {
    marginBottom: 16,
    maxWidth: '85%',
  },
  userAlign: {
    alignSelf: 'flex-end',
  },
  modelAlign: {
    alignSelf: 'flex-start',
  },
  userBubble: {
    borderWidth: 1,
    borderRadius: 20,
    borderTopRightRadius: 4,
    padding: 16,
  },
  modelBubble: {
    borderWidth: 0,
    borderRadius: 20,
    borderTopLeftRadius: 4,
    padding: 16,
  },
  messageText: {
    fontSize: 16,
    lineHeight: 24,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    flexShrink: 0,
  },
  actionRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 12,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 12,
  },
  mainTextInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 16,
    maxHeight: 150,
  },
};

// ============================================================================
// INTRO SCREEN STYLES
// ============================================================================

export const introScreenStyles = {
  container: {
    width: '100%',
    height: '100%',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  backgroundImageStyle: {
    opacity: 0.6,
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  darkOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.3,
  },
  titleSection: {
    position: 'absolute',
    top: '20%',
    left: 0,
    right: 0,
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  content: {
    position: 'absolute',
    bottom: 60,
    left: 0,
    right: 0,
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  buttonContainer: {
    width: '80%',
    maxWidth: 400,
  },
  title: {
    fontWeight: '400',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.8)',
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 8,
    marginBottom: 12,
    fontFamily: 'EagleLake',
  },
  hook: {
    fontWeight: '400',
    fontStyle: 'italic' as const,
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.6)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 4,
    fontFamily: 'EagleLake',
  },
  beginButton: {
    paddingVertical: 18,
    paddingHorizontal: 40,
    borderRadius: 30,
  },
  beginButtonText: {
    color: '#ffffff',
    fontWeight: 'bold',
  },
};

// ============================================================================
// SETUP WIZARD STYLES
// ============================================================================

export const setupWizardStyles = {
  scrollContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    width: '100%',
  },
  card: {
    width: '90%',
    maxWidth: 600,
    padding: 32,
    borderRadius: 16,
    borderWidth: 1,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 32,
  },
  inputGroup: {
    gap: 24,
  },
  fieldContainer: {
    width: '100%',
  },
  label: {
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  pickerWrapper: {
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    minHeight: 48,
  },
  picker: {
    height: 60,
    backgroundColor: 'transparent',
    borderWidth: 0,
    flex: 1,
    fontSize: 16,
    marginTop: 4,
    marginBottom: 4,
  },
  pickerText: {
    flex: 1,
    fontSize: 16,
    lineHeight: 20,
    paddingVertical: 4,
  },
  downArrow: {
    fontSize: 14,
    marginLeft: 8,
  },
  rowWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  inputWithIcon: {
    flex: 1,
    marginRight: 0,
  },
  textInput: {
    minHeight: 48,
  },
  randomizeBtn: {
    width: 44,
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  randomizeBtnVertical: {
    justifyContent: 'flex-start',
    paddingTop: 32,
  },
  randomizeBtnText: {
    fontSize: 18,
  },
  lockedIndicator: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 24,
  },
  modalButtonRow: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 10,
    alignItems: 'center',
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  themeName: {
    fontSize: 16,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 14,
    marginBottom: 8,
  },
  primaryButton: {},
  primaryButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  changeCharBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  changeCharBtnText: {
    fontSize: 14,
    fontWeight: '600',
  },
  backgroundImage: {
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
    flex: 1,
  },
  backgroundImageStyle: {
    opacity: 1,
  },
  closeButton: {
    position: 'absolute',
    top: 20,
    left: 20,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
  },
  closeIcon: {
    fontSize: 24,
    fontWeight: 'bold',
  },
};

// ============================================================================
// NARRATIVE UI STYLES
// ============================================================================

export const narrativeUIStyles = {
  sceneImage: {
    width: '100%',
    height: 250,
    borderRadius: 12,
    marginTop: 12,
  },
  inputControlWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  inputControlButton: {
    height: 40,
    paddingHorizontal: 10,
  },
  editModalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  },
  editModalContainer: {
    width: '90%',
    maxWidth: 500,
    borderRadius: 20,
    borderWidth: 1,
    padding: 24,
  },
  editModalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  editModalSubtitle: {
    fontSize: 14,
    fontStyle: 'italic',
    marginBottom: 20,
  },
  editModalInput: {
    minHeight: 100,
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    marginBottom: 20,
  },
  editModalButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  editModalButton: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  editModalCancelButton: {},
  editModalSaveButton: {},
  editModalButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  mobileSidebarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1500,
  },
  mobileSidebar: {
    width: '85%',
    maxWidth: 320,
    height: '100%',
    elevation: 10,
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
  menuButton: {
    position: 'absolute',
    top: 10,
    left: 10,
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
    elevation: 4,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  inventoryOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  inventoryContainer: {
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
    borderRadius: 16,
    borderWidth: 1,
  },
  inventoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  inventoryTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  inventoryContent: {
    padding: 16,
    maxHeight: 400,
  },
  inventoryItem: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    marginBottom: 8,
  },
  inventoryItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  inventoryItemName: {
    fontSize: 16,
    fontWeight: '600',
  },
  inventoryItemQty: {
    fontSize: 14,
  },
  inventoryItemDesc: {
    fontSize: 14,
    marginTop: 4,
  },
  inventoryItemType: {
    fontSize: 12,
    marginTop: 4,
    fontStyle: 'italic',
  },
  inventoryEmpty: {
    fontSize: 16,
    textAlign: 'center',
    padding: 20,
    fontStyle: 'italic',
  },
  statsSection: {
    padding: 16,
    borderBottomWidth: 1,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 12,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  // Stat progression progress bar styles
  statWithProgressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  progressBarContainer: {
    flex: 2,
    paddingLeft: 12,
  },
  progressBarBackground: {
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 10,
    marginTop: 2,
  },
  // Compact stats grid (2 wide x 3 high) for stat progression
  compactStatsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  compactStatItem: {
    width: '48%',
    marginBottom: 8,
    marginRight: '2%',
  },
  compactStatLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  compactStatLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  compactStatValue: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  compactProgressBarBackground: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  compactProgressBarFill: {
    height: '100%',
    borderRadius: 2,
  },
  compactProgressText: {
    fontSize: 8,
    marginTop: 1,
  },
  // Smaller inventory item styles
  inventoryItemSmall: {
    borderRadius: 6,
    borderWidth: 1,
    padding: 8,
    marginBottom: 6,
  },
  inventoryItemHeaderSmall: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inventoryItemNameSmall: {
    fontSize: 13,
    fontWeight: '600',
  },
  inventoryItemQtySmall: {
    fontSize: 11,
  },
  // Inventory item detail modal styles
  inventoryDetailOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  inventoryDetailContainer: {
    width: '90%',
    maxWidth: 400,
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
  },
  inventoryDetailTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  inventoryDetailType: {
    fontSize: 12,
    fontStyle: 'italic',
    marginBottom: 12,
  },
  inventoryDetailDesc: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 20,
  },
  inventoryDetailButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  inventoryDetailButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  inventoryDetailButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
};

// ============================================================================
// SETTINGS MENU STYLES
// ============================================================================

export const settingsMenuStyles = {
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  menuContainer: {
    width: '90%',
    maxWidth: 500,
    height: '90%',
    borderRadius: 20,
    borderWidth: 1,
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  backButton: {
    padding: 4,
  },
  spacer: {
    width: 50,
  },
  titleContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  menuTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 12,
  },
  navButton: {
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  legalButton: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  tabBar: {
    flexDirection: 'row',
    marginBottom: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tab: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderRightWidth: 1,
  },
  tabLast: {
    borderRightWidth: 0,
  },
  tabText: {
    fontSize: 16,
    fontWeight: '600',
  },
};
